/**
 * Per-user pins — TanStack Query hooks.
 *
 * NAMING: the user-facing word is "favourites" (drawer tab, tree kebab). The
 * hook, REST route and DB table stay "pins" — renaming those crosses an API +
 * DB boundary for a label change. Grep both words when touching this.
 *
 * `userId` semantics mirror useRecents: pass '' while the current user is
 * still loading — the query stays disabled so pins are never fetched (or
 * cached) under the wrong user key.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PinDto, PinnedEntityType } from '@bower/shared';
import { apiService } from '../../services/api.service';
import { createPin, deletePin, listPins, reorderPins } from '../../services/api/pins.api';
import { queryKeys } from '../../lib/queryClient';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { useAppToast } from '../ui/useAppToast';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';

export function usePins(userId: string) {
  const workspaceId = useWorkspaceId();
  return useQuery({
    queryKey: queryKeys.pins.list(workspaceId || '', userId),
    queryFn: async () => {
      const res = await listPins(apiService);
      if (!res.success || !res.data) {
        throw new Error(res.error?.message || 'Failed to load pins');
      }
      return res.data;
    },
    enabled: !!workspaceId && userId !== '',
    staleTime: 30 * 1000,
  });
}

export function usePinMutations(userId: string) {
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();
  const toast = useAppToast();
  const key = queryKeys.pins.list(workspaceId || '', userId);

  const pin = useMutation({
    mutationFn: (input: { entityType: PinnedEntityType; entityId: string }) =>
      createPin(apiService, input),
    onSuccess: (res, input) => {
      if (!res.success) {
        // The API client resolves failure envelopes rather than throwing —
        // surface them here or the cap reads as a silent no-op (rule 12).
        toast({
          title: 'Could not pin',
          description: res.error?.message || 'Something went wrong. Try again.',
          status: 'warning',
        });
        return;
      }
      safeAnalytics.track(ANALYTICS_EVENTS.SIDEBAR_ITEM_PINNED, {
        entity_type: input.entityType,
      } as Record<string, unknown>);
      void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: () => {
      toast({
        title: 'Could not pin',
        description: 'Something went wrong. Try again.',
        status: 'error',
      });
    },
  });

  const unpin = useMutation({
    mutationFn: (pinId: string) => deletePin(apiService, pinId),
    // Optimistic: remove locally, roll back on failure.
    onMutate: async (pinId) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<PinDto[]>(key);
      queryClient.setQueryData<PinDto[]>(key, (old) => old?.filter((p) => p.id !== pinId) ?? []);
      return { previous };
    },
    onSuccess: (res, _pinId, ctx) => {
      if (!res.success) {
        if (ctx?.previous) queryClient.setQueryData(key, ctx.previous);
        toast({
          title: 'Could not unpin',
          description: res.error?.message || 'Something went wrong. Try again.',
          status: 'warning',
        });
        return;
      }
      safeAnalytics.track(ANALYTICS_EVENTS.SIDEBAR_ITEM_UNPINNED);
    },
    onError: (_err, _pinId, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(key, ctx.previous);
      toast({
        title: 'Could not unpin',
        description: 'Something went wrong. Try again.',
        status: 'error',
      });
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: key }),
  });

  const reorder = useMutation({
    // `orderedIds` is the new order of the currently-VISIBLE pins (the only
    // ones the sidebar can render/drag). The endpoint is subset-tolerant.
    mutationFn: (orderedIds: string[]) => reorderPins(apiService, orderedIds),
    // Optimistic: reorder the cached list to match immediately; roll back on
    // failure. Any hidden "ghost" pins (entity restricted/trashed) aren't in
    // the cache, so they keep their server position untouched.
    onMutate: async (orderedIds) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<PinDto[]>(key);
      queryClient.setQueryData<PinDto[]>(key, (old) => {
        if (!old) return old;
        const byId = new Map(old.map((p) => [p.id, p]));
        // Reordered visible pins first (in the new order), then anything not
        // in the list (defensive — shouldn't happen for a full visible set).
        const reordered = orderedIds.map((id) => byId.get(id)).filter((p): p is PinDto => !!p);
        const rest = old.filter((p) => !orderedIds.includes(p.id));
        return [...reordered, ...rest];
      });
      return { previous };
    },
    onSuccess: (res, _ids, ctx) => {
      if (!res.success) {
        if (ctx?.previous) queryClient.setQueryData(key, ctx.previous);
        toast({
          title: 'Could not reorder pins',
          description: res.error?.message || 'Something went wrong. Try again.',
          status: 'warning',
        });
        return;
      }
      safeAnalytics.track(ANALYTICS_EVENTS.SIDEBAR_ITEM_REORDERED, {
        entity_type: 'pin',
      } as Record<string, unknown>);
    },
    onError: (_err, _ids, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(key, ctx.previous);
      toast({
        title: 'Could not reorder pins',
        description: 'Something went wrong. Try again.',
        status: 'error',
      });
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: key }),
  });

  return { pin, unpin, reorder };
}
